import useCapDonVi from "@/core/hooks/use-cap-don-vi";
import { formatDatePayload } from "@/core/hooks/use-date-time-hook";
import { useDashboardStore } from "@/core/hooks/useDashboardStore";
import type {
    DashboardDefaultScope,
    DashboardMapMarker,
    DashboardScope,
    DashboardScopeType,
} from "@/core/interfaces/Dashboard";
import { computed } from "vue";

export function useDashboardScope() {
  const {
    scope,
    selectedMarkerId,
    setScope,
    patchScope,
    setSelectedMarkerId,
    setDateRange,
  } = useDashboardStore();
  const { isCompanyLevel, isUnitLevel } = useCapDonVi();

  const scopeType = computed(() => scope.value?.type ?? null);

  function buildDefaultDateRange(year: number): {
    fromDate: string;
    toDate: string;
  } {
    return {
      fromDate: formatDatePayload(new Date(year, 0, 1)),
      toDate: formatDatePayload(new Date(year, 11, 31)),
    };
  }

  function applyDefaultScopeFromMeta(
    defaultScope: DashboardDefaultScope,
    fromDate: string,
    toDate: string
  ): void {
    const next: DashboardScope = {
      type: defaultScope.type,
      fromDate,
      toDate,
    };

    if (defaultScope.idDonVi != null) {
      next.idDonVi = defaultScope.idDonVi;
    }
    if (defaultScope.idKho != null) {
      next.idKho = defaultScope.idKho;
    }
    if (defaultScope.provinceCode) {
      next.provinceCode = defaultScope.provinceCode;
    }

    setScope(next);
    setSelectedMarkerId(null);
  }

  function drillDownToMarker(marker: DashboardMapMarker): void {
    if (!scope.value) {
      return;
    }

    if (marker.entityType === "unit") {
      patchScope({
        type: "unit",
        idDonVi: marker.id,
        idKho: undefined,
        provinceCode: marker.provinceCode ?? scope.value.provinceCode,
      });
      setSelectedMarkerId(marker.id);
      return;
    }

    if (marker.entityType === "warehouse") {
      patchScope({
        type: "warehouse",
        idKho: marker.id,
        provinceCode: marker.provinceCode ?? scope.value.provinceCode,
      });
      setSelectedMarkerId(marker.id);
    }
  }

  function resetToDefaultScope(
    defaultScope: DashboardDefaultScope,
    fromDate?: string,
    toDate?: string
  ): void {
    applyDefaultScopeFromMeta(
      defaultScope,
      fromDate ?? scope.value?.fromDate ?? "",
      toDate ?? scope.value?.toDate ?? ""
    );
  }

  function canDrillDown(type: DashboardScopeType): boolean {
    if (isCompanyLevel.value) {
      return type === "national" || type === "unit";
    }
    if (isUnitLevel.value) {
      return type === "province";
    }
    return false;
  }

  function updateDateRange(fromDate: string, toDate: string): void {
    setDateRange(fromDate, toDate);
  }

  return {
    scope,
    scopeType,
    selectedMarkerId,
    isCompanyLevel,
    isUnitLevel,
    buildDefaultDateRange,
    applyDefaultScopeFromMeta,
    drillDownToMarker,
    resetToDefaultScope,
    canDrillDown,
    updateDateRange,
    setScope,
    patchScope,
  };
}
